import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus, TrendingUp, Target, Briefcase, Save, Trash2, Edit2, X, User } from 'lucide-react';
import { Chart, registerables } from 'chart.js';
import MatrixBackground from '../../components/ui/MatrixBackground';
import {
  loadMetrics, saveMetrics, loadPostulaciones,
  savePostulacion, updatePostulacion, deletePostulacion, deleteMetric,
  loadPerfil, savePerfil,
  type LinkedInWeek, type Postulacion, type LinkedInPerfil,
} from '../../services/linkedinService';

Chart.register(...registerables);

type Tab = 'metricas' | 'ssi' | 'postulaciones' | 'perfil' | 'cargar';

const METRIC_LABELS: Record<string, string> = {
  publicaciones: 'Publicaciones',
  impresiones: 'Impresiones',
  visualizaciones: 'Visualizaciones',
  seguidores: 'Seguidores',
  busquedas: 'Apariciones en búsquedas',
  contactos_reclutador: 'Contactos reclutador',
  entrevistas: 'Entrevistas realizadas',
  ofertas: 'Ofertas recibidas',
};

const SSI_LABELS: Record<string, string> = {
  ssi_total: 'SSI Total',
  ssi_marca: 'Marca profesional',
  ssi_personas: 'Encontrar personas',
  ssi_interactuar: 'Interactuar',
  ssi_relaciones: 'Crear relaciones',
};

const CHART_COLORS = ['#00ff41','#4ade80','#60a5fa','#f472b6','#a78bfa','#fbbf24','#34d399','#f87171'];

const CUMULATIVE_KEYS = new Set(['contactos_reclutador', 'entrevistas', 'ofertas']);

function cumulate(values: (number | null)[]): (number | null)[] {
  let acc = 0;
  return values.map(v => {
    if (v == null) return null;
    acc += v;
    return acc;
  });
}

function fmt(d: string) {
  const [, m, dd] = d.split('-');
  return `${dd}/${m}`;
}

function deltaText(curr: number | null, prev: number | null): { text: string; cls: string } | null {
  if (curr == null || prev == null) return null;
  const diff = curr - prev;
  if (diff === 0) return { text: '→ 0', cls: 'text-gray-500' };
  const pct = prev !== 0 ? ((diff / prev) * 100).toFixed(0) : '∞';
  const sign = diff > 0 ? '+' : '';
  return {
    text: `${sign}${diff.toLocaleString('es')} (${sign}${pct}%)`,
    cls: diff > 0 ? 'text-green-400' : 'text-red-400',
  };
}

export default function LinkedInMetrics() {
  const { userId: userIdParam } = useParams<{ userId?: string }>();
  const viewingUserId = userIdParam ? Number(userIdParam) : undefined;
  const isAdminView = !!viewingUserId;

  const [tab, setTab] = useState<Tab>('metricas');
  const [metrics, setMetrics] = useState<LinkedInWeek[]>([]);
  const [postulaciones, setPostulaciones] = useState<Postulacion[]>([]);
  const [perfil, setPerfil] = useState<LinkedInPerfil>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const chartsRef = useRef<Record<string, Chart>>({});

  useEffect(() => {
    async function load() {
      const [m, p, pf] = await Promise.all([
        loadMetrics(viewingUserId),
        loadPostulaciones(viewingUserId),
        loadPerfil(viewingUserId),
      ]);
      setMetrics(m);
      setPostulaciones(p);
      setPerfil(pf);
      setLoading(false);
    }
    load();
  }, [viewingUserId]);

  const destroyCharts = useCallback(() => {
    Object.values(chartsRef.current).forEach(c => c.destroy());
    chartsRef.current = {};
  }, []);

  useEffect(() => { return () => destroyCharts(); }, [destroyCharts]);

  const showMsg = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const hasSSI = metrics.some(w => w.ssi_total != null);
  const hasPostulaciones = postulaciones.length > 0;

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'metricas', label: 'Métricas', icon: <TrendingUp size={16} /> },
    ...(hasSSI ? [{ id: 'ssi' as Tab, label: 'SSI', icon: <Target size={16} /> }] : []),
    ...(hasPostulaciones ? [{ id: 'postulaciones' as Tab, label: 'Postulaciones', icon: <Briefcase size={16} /> }] : []),
    ...(!isAdminView ? [{ id: 'perfil' as Tab, label: 'Salario/ingresos', icon: <User size={16} /> }] : []),
    ...(!isAdminView ? [{ id: 'cargar' as Tab, label: 'Cargar datos', icon: <Plus size={16} /> }] : []),
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black font-sans">
      {message && (
        <div className={`fixed top-16 right-4 z-[60] px-4 py-2 rounded-lg text-sm font-medium ${
          message.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {message.text}
        </div>
      )}

      <nav className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-green-500/30 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex justify-between items-center">
            <Link to={isAdminView ? "/admin/linkedin" : "/cursos"} className="flex items-center gap-2 text-green-400/70 hover:text-green-300 transition-colors text-sm font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M15 19l-7-7 7-7" /></svg>
              {isAdminView ? 'Volver al panel' : 'Volver'}
            </Link>
            {!isAdminView && (
              <Link to="/profile" className="text-green-400/70 hover:text-green-300 transition-colors text-sm font-medium">
                Mi perfil
              </Link>
            )}
          </div>
        </div>
      </nav>

      <section className="relative pt-28 pb-6 px-6">
        <MatrixBackground color="#00ff41" animated={false} />
        <div className="absolute inset-0 bg-black/70 z-[1]" />
        <div className="relative z-10 max-w-5xl mx-auto">
          <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight mb-2">
            <span className="text-white">{isAdminView ? 'LinkedIn' : 'Mi LinkedIn'}</span>
            <span className="text-green-400/60 font-normal text-lg md:text-xl ml-3">Evolución</span>
          </h1>
          {isAdminView && (
            <p className="text-sm text-gray-400 mb-2">Vista admin — usuario #{viewingUserId}</p>
          )}
          <a
            href="https://www.youtube.com/watch?v=E6vHuMqe_YI"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-red-600/20 border border-red-500/30 rounded-lg text-sm text-red-400 hover:bg-red-600/30 transition-all"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            ¿Cómo cargo mis métricas? — Ver video explicativo
          </a>

          <div className="flex gap-1 mt-5 flex-wrap">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => { destroyCharts(); setTab(t.id); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all ${
                  tab === t.id
                    ? 'border-green-500 bg-green-600/20 text-green-400 font-semibold'
                    : 'border-green-500/20 bg-black/50 text-gray-400 hover:border-green-500/50 hover:text-green-300'
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="relative pb-24 px-6">
        <MatrixBackground color="#00ff41" animated={false} />
        <div className="absolute inset-0 bg-black/70 z-[1]" />
        <div className="relative z-10 max-w-5xl mx-auto">
          {tab === 'metricas' && <MetricasTab metrics={metrics} setMetrics={setMetrics} chartsRef={chartsRef} showMsg={showMsg} />}
          {tab === 'ssi' && <SSITab metrics={metrics} chartsRef={chartsRef} />}
          {tab === 'postulaciones' && (
            <PostulacionesTab
              postulaciones={postulaciones}
              setPostulaciones={setPostulaciones}
              showMsg={showMsg}
            />
          )}
          {tab === 'perfil' && (
            <PerfilTab
              perfil={perfil}
              setPerfil={setPerfil}
              showMsg={showMsg}
            />
          )}
          {tab === 'cargar' && (
            <CargarTab
              saving={saving}
              setSaving={setSaving}
              metrics={metrics}
              setMetrics={setMetrics}
              postulaciones={postulaciones}
              setPostulaciones={setPostulaciones}
              showMsg={showMsg}
              setTab={setTab}
            />
          )}
        </div>
      </section>
    </div>
  );
}

function SummaryCard({ value, label, color }: { value: string; label: string; color?: string }) {
  return (
    <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-4 text-center">
      <div className="text-2xl font-bold" style={{ color: color || '#4ade80' }}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function MetricasTab({ metrics, setMetrics, chartsRef, showMsg }: {
  metrics: LinkedInWeek[];
  setMetrics: React.Dispatch<React.SetStateAction<LinkedInWeek[]>>;
  chartsRef: React.MutableRefObject<Record<string, Chart>>;
  showMsg: (t: string, type: 'success' | 'error') => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const separateContainerRef = useRef<HTMLDivElement>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [chartMode, setChartMode] = useState<'unified' | 'separate'>('unified');

  const handleDeleteWeek = async (id: number) => {
    if (!window.confirm('¿Eliminar este registro de métricas?')) return;
    const ok = await deleteMetric(id);
    if (ok) {
      setMetrics(prev => prev.filter(w => w.id !== id));
      showMsg('Registro eliminado', 'success');
    } else {
      showMsg('Error al eliminar', 'error');
    }
  };

  const metricKeys = Object.keys(METRIC_LABELS).filter(k =>
    metrics.some(w => (w as any)[k] != null)
  );

  useEffect(() => {
    if (visibleKeys.size === 0 && metricKeys.length > 0) {
      setVisibleKeys(new Set(metricKeys));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricKeys.length]);

  const lastVal = (key: string) => {
    for (let i = metrics.length - 1; i >= 0; i--) {
      const v = (metrics[i] as any)[key];
      if (v != null) return v as number;
    }
    return null;
  };

  const firstVal = (key: string) => {
    for (let i = 0; i < metrics.length; i++) {
      const v = (metrics[i] as any)[key];
      if (v != null) return v as number;
    }
    return null;
  };

  const growthPct = (key: string) => {
    const f = firstVal(key), l = lastVal(key);
    if (f == null || l == null || f === 0) return null;
    return ((l - f) / f * 100).toFixed(0);
  };

  const toggleKey = (key: string) => {
    setVisibleKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (chartMode !== 'unified') return;
    if (!canvasRef.current || metrics.length < 2 || visibleKeys.size === 0) return;

    const labels = metrics.map(w => fmt(w.week_date));
    const datasets = metricKeys
      .filter(k => visibleKeys.has(k))
      .map((key) => {
        const color = CHART_COLORS[metricKeys.indexOf(key) % CHART_COLORS.length];
        const raw = metrics.map(w => (w as any)[key] as number | null);
        const data = CUMULATIVE_KEYS.has(key) ? cumulate(raw) : raw;
        return {
          label: METRIC_LABELS[key] + (CUMULATIVE_KEYS.has(key) ? ' (acum.)' : ''),
          data,
          borderColor: color,
          backgroundColor: color + '22',
          fill: false,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 3,
          spanGaps: true,
        };
      });

    const chart = new Chart(canvasRef.current.getContext('2d')!, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { mode: 'index', intersect: false },
        },
        scales: {
          x: { ticks: { color: '#6b7280', font: { size: 10 } }, grid: { color: 'rgba(0,255,65,.05)' } },
          y: { beginAtZero: true, ticks: { color: '#6b7280', font: { size: 10 } }, grid: { color: 'rgba(0,255,65,.08)' } },
        },
      },
    });
    chartsRef.current['unified'] = chart;

    return () => {
      chart.destroy();
      delete chartsRef.current['unified'];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics, visibleKeys, chartMode]);

  useEffect(() => {
    if (chartMode !== 'separate') return;
    if (!separateContainerRef.current || metrics.length < 2) return;
    const container = separateContainerRef.current;
    container.innerHTML = '';

    const activeKeys = metricKeys.filter(k => visibleKeys.has(k));
    const localCharts: Chart[] = [];

    activeKeys.forEach((key) => {
      const labels: string[] = [];
      const rawValues: number[] = [];
      metrics.forEach(w => {
        const val = (w as any)[key];
        if (val != null) { labels.push(fmt(w.week_date)); rawValues.push(val); }
      });
      if (rawValues.length < 2) return;

      const isCumulative = CUMULATIVE_KEYS.has(key);
      const values = isCumulative ? cumulate(rawValues) as number[] : rawValues;

      const card = document.createElement('div');
      card.className = 'bg-black/60 border border-green-500/20 rounded-xl p-4';
      const title = document.createElement('h3');
      title.className = 'text-xs text-gray-500 uppercase tracking-wider mb-2';
      title.textContent = METRIC_LABELS[key] + (isCumulative ? ' (acumulado)' : '');
      card.appendChild(title);
      const canvas = document.createElement('canvas');
      card.appendChild(canvas);
      container.appendChild(card);

      const color = CHART_COLORS[metricKeys.indexOf(key) % CHART_COLORS.length];
      const chart = new Chart(canvas.getContext('2d')!, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            data: values,
            borderColor: color,
            backgroundColor: color + '22',
            fill: true,
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 4,
          }],
        },
        options: {
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { color: '#6b7280', font: { size: 10 } }, grid: { color: 'rgba(0,255,65,.05)' } },
            y: { beginAtZero: true, ticks: { color: '#6b7280', font: { size: 10 } }, grid: { color: 'rgba(0,255,65,.08)' } },
          },
        },
      });
      localCharts.push(chart);
    });

    return () => { localCharts.forEach(c => c.destroy()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics, visibleKeys, chartMode]);

  if (metrics.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <TrendingUp size={48} className="mx-auto mb-4 opacity-30" />
        <p>Todavía no cargaste métricas.</p>
        <p className="text-sm mt-1">Usá la pestaña "Cargar datos" para agregar tu primera medición.</p>
      </div>
    );
  }

  const segG = growthPct('seguidores');
  const impG = growthPct('impresiones');

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard value={String(metrics.length)} label="Mediciones" />
        {lastVal('seguidores') != null && (
          <SummaryCard
            value={lastVal('seguidores')!.toLocaleString('es')}
            label={`Seguidores${segG ? ` (${Number(segG) > 0 ? '+' : ''}${segG}%)` : ''}`}
          />
        )}
        {lastVal('impresiones') != null && (
          <SummaryCard
            value={lastVal('impresiones')!.toLocaleString('es')}
            label={`Impresiones${impG ? ` (${Number(impG) > 0 ? '+' : ''}${impG}%)` : ''}`}
          />
        )}
        {lastVal('publicaciones') != null && (
          <SummaryCard value={String(lastVal('publicaciones'))} label="Publicaciones (última)" />
        )}
      </div>

      <details className="bg-gray-900/80 border border-green-500/20 rounded-xl mb-6">
        <summary className="cursor-pointer p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-base font-semibold text-green-400">Actividad LinkedIn</h2>
          <span className="text-xs text-gray-500">Click para expandir</span>
        </summary>
        <div className="p-4 md:p-6 pt-0 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left text-green-400 text-xs uppercase tracking-wider p-2">Métrica</th>
                {metrics.map(w => (
                  <th key={w.week_date} className="text-right text-green-400 text-xs uppercase tracking-wider p-2 whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      {fmt(w.week_date)}
                      <button
                        onClick={() => handleDeleteWeek(w.id!)}
                        className="text-gray-600 hover:text-red-400 transition-colors ml-1"
                        title="Eliminar registro"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metricKeys.map(key => (
                <tr key={key} className="border-t border-green-500/10">
                  <td className="p-2 whitespace-nowrap text-gray-300">{METRIC_LABELS[key]}</td>
                  {metrics.map((w, i) => {
                    const val = (w as any)[key] as number | null;
                    const prev = i > 0 ? (metrics[i - 1] as any)[key] as number | null : null;
                    const d = i > 0 ? deltaText(val, prev) : null;
                    return (
                      <td key={w.week_date} className="text-right p-2 whitespace-nowrap text-gray-300">
                        {val != null ? val.toLocaleString('es') : '—'}
                        {d && <span className={`block text-[10px] ${d.cls}`}>{d.text}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* Gráfico con toggles */}
      {metrics.length >= 2 && (
        <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-4 md:p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-green-400">Evolución</h2>
            <div className="flex items-center gap-1 bg-black/40 rounded-lg p-0.5">
              <button
                onClick={() => setChartMode('unified')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  chartMode === 'unified' ? 'bg-green-600/30 text-green-400' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                Unificado
              </button>
              <button
                onClick={() => setChartMode('separate')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  chartMode === 'separate' ? 'bg-green-600/30 text-green-400' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                Separados
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {metricKeys.map((key, idx) => {
              const color = CHART_COLORS[idx % CHART_COLORS.length];
              const active = visibleKeys.has(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleKey(key)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    active
                      ? 'border-transparent text-black'
                      : 'border-gray-600 text-gray-500 bg-transparent opacity-50'
                  }`}
                  style={active ? { backgroundColor: color } : undefined}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  {METRIC_LABELS[key]}
                </button>
              );
            })}
          </div>
          {chartMode === 'unified' && <canvas ref={canvasRef} />}
          {chartMode === 'separate' && (
            <div ref={separateContainerRef} className="grid grid-cols-1 md:grid-cols-2 gap-4" />
          )}
        </div>
      )}
    </>
  );
}

function SSITab({ metrics, chartsRef }: { metrics: LinkedInWeek[]; chartsRef: React.MutableRefObject<Record<string, Chart>> }) {
  const radarRef = useRef<HTMLCanvasElement>(null);
  const ssiLineContainerRef = useRef<HTMLDivElement>(null);
  const ssiWeeks = metrics.filter(w => w.ssi_total != null);

  const lastVal = (key: string) => {
    for (let i = ssiWeeks.length - 1; i >= 0; i--) {
      const v = (ssiWeeks[i] as any)[key];
      if (v != null) return v as number;
    }
    return null;
  };

  useEffect(() => {
    if (!radarRef.current || ssiWeeks.length === 0) return;
    const last = ssiWeeks[ssiWeeks.length - 1];
    const chart = new Chart(radarRef.current.getContext('2d')!, {
      type: 'radar',
      data: {
        labels: ['Marca', 'Personas', 'Interactuar', 'Relaciones'],
        datasets: [{
          data: [last.ssi_marca, last.ssi_personas, last.ssi_interactuar, last.ssi_relaciones],
          borderColor: '#00ff41',
          backgroundColor: 'rgba(0,255,65,.15)',
          borderWidth: 2,
          pointRadius: 4,
          pointBackgroundColor: '#00ff41',
        }],
      },
      options: {
        responsive: true,
        scales: {
          r: {
            beginAtZero: true,
            max: 25,
            ticks: { color: '#6b7280', backdropColor: 'transparent' },
            grid: { color: 'rgba(0,255,65,.1)' },
            pointLabels: { color: '#d1d5db', font: { size: 12 } },
          },
        },
        plugins: { legend: { display: false } },
      },
    });
    chartsRef.current['ssi'] = chart;

    return () => {
      chart.destroy();
      delete chartsRef.current['ssi'];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics]);

  useEffect(() => {
    if (!ssiLineContainerRef.current || ssiWeeks.length < 2) return;
    const container = ssiLineContainerRef.current;
    container.innerHTML = '';

    const ssiKeys = ['ssi_total', 'ssi_marca', 'ssi_personas', 'ssi_interactuar', 'ssi_relaciones'];
    const ssiColors = ['#00ff41', '#4ade80', '#60a5fa', '#f472b6', '#a78bfa'];
    const localCharts: Chart[] = [];

    ssiKeys.forEach((key, idx) => {
      const labels: string[] = [];
      const values: number[] = [];
      ssiWeeks.forEach(w => {
        const val = (w as any)[key];
        if (val != null) { labels.push(fmt(w.week_date)); values.push(val); }
      });
      if (values.length < 2) return;

      const card = document.createElement('div');
      card.className = 'bg-black/60 border border-green-500/20 rounded-xl p-4';
      const title = document.createElement('h3');
      title.className = 'text-xs text-gray-500 uppercase tracking-wider mb-2';
      title.textContent = SSI_LABELS[key];
      card.appendChild(title);
      const canvas = document.createElement('canvas');
      card.appendChild(canvas);
      container.appendChild(card);

      const color = ssiColors[idx];
      const chart = new Chart(canvas.getContext('2d')!, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            data: values,
            borderColor: color,
            backgroundColor: color + '22',
            fill: true,
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 4,
          }],
        },
        options: {
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { color: '#6b7280', font: { size: 10 } }, grid: { color: 'rgba(0,255,65,.05)' } },
            y: { beginAtZero: true, max: key === 'ssi_total' ? 100 : 25, ticks: { color: '#6b7280', font: { size: 10 } }, grid: { color: 'rgba(0,255,65,.08)' } },
          },
        },
      });
      localCharts.push(chart);
    });

    return () => { localCharts.forEach(c => c.destroy()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics]);

  if (ssiWeeks.length === 0) return <p className="text-gray-500 text-center py-8">Sin datos de SSI.</p>;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <SummaryCard value={`${lastVal('ssi_total') ?? 0}/100`} label="SSI Total" />
        <SummaryCard value={`${lastVal('ssi_marca') ?? 0}/25`} label="Marca profesional" />
        <SummaryCard value={`${lastVal('ssi_personas') ?? 0}/25`} label="Encontrar personas" />
        <SummaryCard value={`${lastVal('ssi_interactuar') ?? 0}/25`} label="Interactuar" />
        <SummaryCard value={`${lastVal('ssi_relaciones') ?? 0}/25`} label="Crear relaciones" />
      </div>

      <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-4 md:p-6 mb-6 overflow-x-auto">
        <h2 className="text-base font-semibold text-green-400 mb-4">Evolución SSI</h2>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left text-green-400 text-xs uppercase p-2">Componente</th>
              {ssiWeeks.map(w => (
                <th key={w.week_date} className="text-right text-green-400 text-xs uppercase p-2 whitespace-nowrap">{fmt(w.week_date)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(SSI_LABELS).map(key => (
              <tr key={key} className="border-t border-green-500/10">
                <td className="p-2 text-gray-300">{SSI_LABELS[key]}</td>
                {ssiWeeks.map((w, i) => {
                  const val = (w as any)[key] as number | null;
                  const prev = i > 0 ? (ssiWeeks[i - 1] as any)[key] as number | null : null;
                  const d = i > 0 ? deltaText(val, prev) : null;
                  return (
                    <td key={w.week_date} className="text-right p-2 whitespace-nowrap text-gray-300">
                      {val ?? '—'}
                      {d && <span className={`block text-[10px] ${d.cls}`}>{d.text}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t border-green-500/10">
              <td className="p-2 text-gray-300">Posicionamiento</td>
              {ssiWeeks.map((w, i) => {
                const pos = (w as any).posicionamiento as string | null;
                const prevPos = i > 0 ? (ssiWeeks[i - 1] as any).posicionamiento as string | null : null;
                const changed = i > 0 && pos && prevPos && pos !== prevPos;
                return (
                  <td key={w.week_date} className={`text-right p-2 whitespace-nowrap text-xs ${changed ? 'text-yellow-400 font-medium' : 'text-gray-400'}`}>
                    {pos || '—'}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="max-w-md mx-auto mb-6">
        <canvas ref={radarRef} />
      </div>

      {ssiWeeks.length >= 2 && (
        <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-4 md:p-6">
          <h2 className="text-base font-semibold text-green-400 mb-4">Evolución por componente</h2>
          <div ref={ssiLineContainerRef} className="grid grid-cols-1 md:grid-cols-2 gap-4" />
        </div>
      )}
    </>
  );
}

function PostulacionesTab({
  postulaciones, setPostulaciones, showMsg,
}: {
  postulaciones: Postulacion[];
  setPostulaciones: React.Dispatch<React.SetStateAction<Postulacion[]>>;
  showMsg: (t: string, type: 'success' | 'error') => void;
}) {
  const [editId, setEditId] = useState<number | null>(null);
  const [editFase, setEditFase] = useState(0);

  const total = postulaciones.length;
  const inbound = postulaciones.filter(p => p.origen === 'inbound').length;
  const responded = postulaciones.filter(p => p.fase > 0).length;
  const offers = postulaciones.filter(p => p.fase === 3).length;
  const rr = total ? ((responded / total) * 100).toFixed(0) : '0';

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar esta postulación?')) return;
    const ok = await deletePostulacion(id);
    if (ok) {
      setPostulaciones(prev => prev.filter(p => p.id !== id));
      showMsg('Postulación eliminada', 'success');
    } else {
      showMsg('Error al eliminar', 'error');
    }
  };

  const handleUpdateFase = async (id: number) => {
    const ok = await updatePostulacion({ id, fase: editFase });
    if (ok) {
      setPostulaciones(prev => prev.map(p => p.id === id ? { ...p, fase: editFase } : p));
      setEditId(null);
      showMsg('Fase actualizada', 'success');
    } else {
      showMsg('Error al actualizar', 'error');
    }
  };

  const faseLabels = ['Aplicado', 'Respuesta', 'Entrevista', 'Oferta'];
  const faseColors = ['text-red-400', 'text-yellow-400', 'text-blue-400', 'text-green-400'];

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard value={String(total)} label="Total postulaciones" />
        <SummaryCard value={String(inbound)} label="Me contactaron" color="#60a5fa" />
        <SummaryCard value={`${rr}%`} label="Tasa de respuesta" />
        <SummaryCard value={String(offers)} label="Ofertas" color={offers ? '#4ade80' : '#f87171'} />
      </div>

      <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-4 md:p-6 overflow-x-auto">
        <h2 className="text-base font-semibold text-green-400 mb-4">Postulaciones</h2>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left text-green-400 text-xs uppercase p-2">#</th>
              <th className="text-left text-green-400 text-xs uppercase p-2">Empresa</th>
              <th className="text-left text-green-400 text-xs uppercase p-2">Puesto</th>
              <th className="text-right text-green-400 text-xs uppercase p-2">Fecha</th>
              <th className="text-center text-green-400 text-xs uppercase p-2">Origen</th>
              <th className="text-center text-green-400 text-xs uppercase p-2">Fase</th>
              <th className="text-center text-green-400 text-xs uppercase p-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {postulaciones.map((p, i) => (
              <tr key={p.id} className="border-t border-green-500/10">
                <td className="p-2 text-gray-300">{i + 1}</td>
                <td className="p-2 text-gray-300">{p.empresa}</td>
                <td className="p-2 text-gray-300">{p.puesto}</td>
                <td className="p-2 text-right whitespace-nowrap text-gray-300">{fmt(p.fecha)}</td>
                <td className="p-2 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                    p.origen === 'inbound' ? 'bg-green-400/10 text-green-400' : 'bg-blue-400/10 text-blue-400'
                  }`}>
                    {p.origen === 'inbound' ? 'Me contactaron' : 'Me postulé'}
                  </span>
                </td>
                <td className="p-2 text-center">
                  {editId === p.id ? (
                    <div className="flex items-center gap-1 justify-center">
                      <select
                        value={editFase}
                        onChange={e => setEditFase(Number(e.target.value))}
                        className="bg-black border border-green-500/30 text-xs rounded px-1 py-0.5 text-gray-300"
                      >
                        {faseLabels.map((l, idx) => <option key={idx} value={idx}>{l}</option>)}
                      </select>
                      <button onClick={() => handleUpdateFase(p.id!)} className="text-green-400 hover:text-green-300"><Save size={14} /></button>
                      <button onClick={() => setEditId(null)} className="text-gray-500 hover:text-gray-300"><X size={14} /></button>
                    </div>
                  ) : (
                    <span className={`text-xs font-semibold ${faseColors[p.fase]}`}>{faseLabels[p.fase]}</span>
                  )}
                </td>
                <td className="p-2 text-center">
                  <div className="flex items-center gap-1 justify-center">
                    <button onClick={() => { setEditId(p.id!); setEditFase(p.fase); }} className="text-gray-500 hover:text-green-400"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(p.id!)} className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PerfilTab({
  perfil, setPerfil, showMsg,
}: {
  perfil: LinkedInPerfil;
  setPerfil: React.Dispatch<React.SetStateAction<LinkedInPerfil>>;
  showMsg: (t: string, type: 'success' | 'error') => void;
}) {
  const [form, setForm] = useState<LinkedInPerfil>(perfil);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(perfil); }, [perfil]);

  const handleSave = async () => {
    setSaving(true);
    const payload: any = {};
    if (form.salario_actual) payload.salario_actual = Number(String(form.salario_actual).replace(',', '.'));
    if (form.salario_nuevo) payload.salario_nuevo = Number(String(form.salario_nuevo).replace(',', '.'));
    const result = await savePerfil(payload);
    setSaving(false);
    if (result) {
      setPerfil(result);
      showMsg('Perfil actualizado', 'success');
    } else {
      showMsg('Error al guardar', 'error');
    }
  };

  const inputCls = 'w-full bg-black/60 border border-green-500/20 rounded-lg p-2.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-green-500/50';
  const labelCls = 'block text-xs text-gray-400 mb-1';

  const salarioActual = perfil.salario_actual ? Number(perfil.salario_actual) : null;
  const salarioNuevo = perfil.salario_nuevo ? Number(perfil.salario_nuevo) : null;
  const delta = salarioActual && salarioNuevo ? ((salarioNuevo - salarioActual) / salarioActual * 100).toFixed(0) : null;

  return (
    <div className="space-y-6">
      {/* Resumen si hay datos */}
      {(salarioActual || salarioNuevo) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {salarioActual != null && <SummaryCard value={`USD ${salarioActual.toLocaleString('es')}`} label="Salario inicial" />}
          {salarioNuevo != null && <SummaryCard value={`USD ${salarioNuevo.toLocaleString('es')}`} label="Salario nuevo" />}
          {delta && <SummaryCard value={`+${delta}%`} label="Incremento" />}
        </div>
      )}

      <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-6 space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-green-400 mb-1">Salario</h3>
          <p className="text-xs text-gray-500 mb-3">Confidencial, nadie lo ve excepto vos. Lo usamos para fines estadísticos.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Salario actual (USD)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="Ej: 1500"
                value={form.salario_actual ?? ''}
                onChange={e => setForm(p => ({ ...p, salario_actual: e.target.value as any }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Salario nuevo (USD)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="Cuando consigas oferta"
                value={form.salario_nuevo ?? ''}
                onChange={e => setForm(p => ({ ...p, salario_nuevo: e.target.value as any }))}
                className={inputCls}
              />
            </div>
          </div>
        </div>


        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-green-600 hover:bg-green-500 text-black font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? 'Guardando...' : 'Guardar perfil'}
        </button>
      </div>
    </div>
  );
}

function CargarTab({
  saving, setSaving, metrics, setMetrics, postulaciones, setPostulaciones, showMsg, setTab,
}: {
  saving: boolean;
  setSaving: (v: boolean) => void;
  metrics: LinkedInWeek[];
  setMetrics: React.Dispatch<React.SetStateAction<LinkedInWeek[]>>;
  postulaciones: Postulacion[];
  setPostulaciones: React.Dispatch<React.SetStateAction<Postulacion[]>>;
  showMsg: (t: string, type: 'success' | 'error') => void;
  setTab: (t: Tab) => void;
}) {
  const [formType, setFormType] = useState<'metricas' | 'postulacion'>('metricas');
  const today = new Date().toISOString().split('T')[0];

  const [weekDate, setWeekDate] = useState(today);
  const [mForm, setMForm] = useState<Record<string, string>>({});
  const [ssiForm, setSSIForm] = useState<Record<string, string>>({});
  const [posicionamiento, setPosicionamiento] = useState('');

  const [pEmpresa, setPEmpresa] = useState('');
  const [pPuesto, setPPuesto] = useState('');
  const [pFecha, setPFecha] = useState(today);
  const [pOrigen, setPOrigen] = useState<'outbound' | 'inbound'>('outbound');
  const [pFase, setPFase] = useState(0);
  const [pNotas, setPNotas] = useState('');

  const handleSaveMetrics = async () => {
    const defaultZeroKeys = ['contactos_reclutador', 'entrevistas', 'ofertas'];
    const filledForm = { ...mForm };
    defaultZeroKeys.forEach(k => {
      if (filledForm[k] === undefined || filledForm[k] === '') filledForm[k] = '0';
    });

    const missingFields = Object.keys(METRIC_LABELS).filter(k => filledForm[k] === undefined || filledForm[k] === '');
    if (missingFields.length > 0) {
      showMsg('Completá todas las métricas de actividad', 'error');
      return;
    }
    const missingSSI = Object.keys(SSI_LABELS).filter(k => ssiForm[k] === undefined || ssiForm[k] === '');
    if (missingSSI.length > 0) {
      showMsg('Completá todos los campos de SSI', 'error');
      return;
    }
    setSaving(true);
    const toNum = (v: string | undefined) => {
      if (!v || v === '') return 0;
      return Number(v.replace(',', '.'));
    };
    const payload: any = { week_date: weekDate };
    Object.keys(METRIC_LABELS).forEach(k => {
      payload[k] = toNum(filledForm[k]);
    });
    ['ssi_total','ssi_marca','ssi_personas','ssi_interactuar','ssi_relaciones'].forEach(k => {
      if (ssiForm[k] !== undefined && ssiForm[k] !== '') payload[k] = toNum(ssiForm[k]);
    });
    if (posicionamiento.trim()) payload.posicionamiento = posicionamiento.trim();

    const ok = await saveMetrics(payload);
    setSaving(false);
    if (ok) {
      showMsg('Métricas guardadas', 'success');
      const fresh = await loadMetrics();
      setMetrics(fresh);
      setTab('metricas');
    } else {
      showMsg('Error al guardar', 'error');
    }
  };

  const handleSavePostulacion = async () => {
    if (!pEmpresa.trim() || !pPuesto.trim()) {
      showMsg('Empresa y puesto son requeridos', 'error');
      return;
    }
    setSaving(true);
    const id = await savePostulacion({
      empresa: pEmpresa.trim(),
      puesto: pPuesto.trim(),
      fecha: pFecha,
      origen: pOrigen,
      fase: pFase,
      notas: pNotas.trim() || null,
    });
    setSaving(false);
    if (id) {
      showMsg('Postulación guardada', 'success');
      const fresh = await loadPostulaciones();
      setPostulaciones(fresh);
      setPEmpresa(''); setPPuesto(''); setPNotas('');
    } else {
      showMsg('Error al guardar', 'error');
    }
  };

  const inputCls = "w-full bg-black/60 border border-green-500/20 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-green-500 focus:outline-none transition-colors";
  const labelCls = "block text-xs text-gray-500 mb-1";

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFormType('metricas')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            formType === 'metricas' ? 'bg-green-600/20 text-green-400 border border-green-500/30' : 'bg-gray-900/80 text-gray-400 border border-green-500/20'
          }`}
        >
          Métricas semanales
        </button>
        <button
          onClick={() => setFormType('postulacion')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            formType === 'postulacion' ? 'bg-green-600/20 text-green-400 border border-green-500/30' : 'bg-gray-900/80 text-gray-400 border border-green-500/20'
          }`}
        >
          Nueva postulación / Me contactaron de
        </button>
      </div>

      {formType === 'metricas' ? (
        <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-6 space-y-6">
          <div>
            <label className={labelCls}>Fecha de la medición</label>
            <input type="date" value={weekDate} onChange={e => setWeekDate(e.target.value)} className={inputCls} />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-green-400 mb-1">Actividad y alcance</h3>
            <p className="text-xs text-gray-500 mb-3">Datos de los últimos 7 días</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(METRIC_LABELS).map(([key, label]) => (
                <div key={key}>
                  <label className={labelCls}>{label}</label>
                  <input
                    type="number"
                    min="0"
                    required
                    placeholder="0"
                    value={mForm[key] ?? ''}
                    onChange={e => setMForm(p => ({ ...p, [key]: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-green-400">SSI</h3>
              <a
                href="https://www.linkedin.com/sales/ssi"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 underline"
              >
                Ver mi SSI en LinkedIn →
              </a>
            </div>
            <p className="text-xs text-gray-500 mb-3">Ingresá al link, copiá tus datos y completá acá</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(SSI_LABELS).map(([key, label]) => (
                <div key={key}>
                  <label className={labelCls}>{label}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    required
                    placeholder="0"
                    value={ssiForm[key] ?? ''}
                    onChange={e => setSSIForm(p => ({ ...p, [key]: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              ))}
              <div>
                <label className={labelCls}>Posicionamiento</label>
                <input
                  type="text"
                  placeholder="Ej: Data Engineer"
                  value={posicionamiento}
                  onChange={e => setPosicionamiento(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleSaveMetrics}
            disabled={saving}
            className="w-full bg-green-600 hover:bg-green-500 text-black font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar métricas'}
          </button>
        </div>
      ) : (
        <div className="bg-gray-900/80 border border-green-500/20 rounded-xl p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Empresa</label>
              <input type="text" value={pEmpresa} onChange={e => setPEmpresa(e.target.value)} placeholder="Google" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Puesto</label>
              <input type="text" value={pPuesto} onChange={e => setPPuesto(e.target.value)} placeholder="Data Engineer Jr" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input type="date" value={pFecha} onChange={e => setPFecha(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Origen</label>
              <select value={pOrigen} onChange={e => setPOrigen(e.target.value as any)} className={inputCls}>
                <option value="outbound">Me postulé</option>
                <option value="inbound">Me contactaron</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Fase</label>
              <select value={pFase} onChange={e => setPFase(Number(e.target.value))} className={inputCls}>
                <option value={0}>Aplicado</option>
                <option value={1}>Respuesta</option>
                <option value={2}>Entrevista</option>
                <option value={3}>Oferta</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Notas (opcional)</label>
              <input type="text" value={pNotas} onChange={e => setPNotas(e.target.value)} placeholder="Recruiter por LinkedIn" className={inputCls} />
            </div>
          </div>

          <button
            onClick={handleSavePostulacion}
            disabled={saving}
            className="w-full bg-green-600 hover:bg-green-500 text-black font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar postulación'}
          </button>
        </div>
      )}
    </div>
  );
}
